var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/* eslint-disable max-lines-per-function */
import { differenceInCalendarDays } from 'date-fns';
import { attentionRequiredStatus, autopayScheduledStatus, closedWithBalanceStatus, currentPaymentDueStatus, enrolledOnPaymentPlanStatus, futurePaymentDueStatus, getPaymentProcessingStatus, getScheduledPaymentStatus, introRateLoanExpiredNotification, introRateLoanExpiringNotification, loanApprovedStillProcessingNotification, loanPaidOffStatus, noPaymentDueStatus, pastDueStatus, registerAccountStatus, waivedInterestExpiredNotification, waivedInterestExpiringNotification } from './notificationStatus';
import { getNumberOfDaysToAutopay, getNumberOfDaysToScheduledPayment, getProcessingActivities, getScheduledActivities, numberOfDaysToDueDate } from './activityHelpers';
import { AUTH_PROHIBITED, CLOSED_WITH_BALANCE, DELINQUENT, INSTALLMENT_LOAN, INSTALLMENT_LOAN_MONTHLY, INSTALLMENT_LOAN_PAY_IN_FOUR, OPEN, PRE_DELIVERY, WAIVED_INTEREST } from '../const/products';
import getScheduledAutopayAmount from './getScheduledAutopayAmount';
/**
 * Determines and returns a list of payment-related notifications based on account details.
 *
 * This function evaluates various conditions, such as account activity, billing details,
 * enrollment status, and product type, to generate a list of applicable notifications.
 * It takes into account factors like past due status, payment schedules, promotional periods,
 * and autopay enrollment to create an accurate and prioritized notification set.
 *
 * The order of the notifications is as follows:
 * 1. Your payment is past due
 * 2. Your payment of $0 is processing
 * 3. Your payment is due today
 * 4. Your payment is due in 1 day
 * 5. Your payment is due in X days
 * 6. Scheduled Payment of $0 will occur in X days
 * 7. No minimum payment due
 * 8. Autopay of $0 will occur in X
 * 9. Autopay of $0 will occur in 1 day
 * 10. Autopay of $0 will occur in X days
 * 11. Promotional period expiring
 * 12. Promotional period expired
 * 13. Intro period expiring
 * 14. Intro period expired
 * 15. Loan approved but still processing
 * 16. Register now to access your account
 * 17. Congratulations, this loan is paid off!
 * 18. This account is closed
 * 19. Enrolled in payment plan
 * ?. Your attention is required (To be defined)
 */
var getNotifications = function (accountDetails) {
    var _a, _b, _c, _d;
    var activity = accountDetails.activity, billing = accountDetails.billing, conditions = accountDetails.conditions, credit = accountDetails.credit, isRegistered = accountDetails.isRegistered, product = accountDetails.product, enrollments = accountDetails.enrollments;
    var _e = billing || {}, pastDue = _e.pastDue, nextPaymentDate = _e.nextPaymentDate, totalMinimumPaymentDue = _e.totalMinimumPaymentDue, customerAssistancePlan = _e.customerAssistancePlan, settlementEnrolled = _e.settlementEnrolled, autopayEnrollment = _e.autopayEnrollment, waivedInterest = _e.waivedInterest, introRateLoan = _e.introRateLoan;
    var daysUntilNextPaymentDue = numberOfDaysToDueDate(nextPaymentDate);
    var isClosedWithBalance = conditions === null || conditions === void 0 ? void 0 : conditions.includes(CLOSED_WITH_BALANCE);
    var isDelinquent = conditions === null || conditions === void 0 ? void 0 : conditions.includes(DELINQUENT);
    var isAttentionRequired = conditions === null || conditions === void 0 ? void 0 : conditions.includes(AUTH_PROHIBITED);
    var isOpen = conditions === null || conditions === void 0 ? void 0 : conditions.includes(OPEN);
    var isPreDelivery = conditions === null || conditions === void 0 ? void 0 : conditions.includes(PRE_DELIVERY);
    var isWaivedInterest = (enrollments === null || enrollments === void 0 ? void 0 : enrollments.includes(WAIVED_INTEREST)) &&
        product === INSTALLMENT_LOAN_MONTHLY;
    var isIntroRateLoan = accountDetails.featureFlags.isIRLEnabled &&
        (enrollments === null || enrollments === void 0 ? void 0 : enrollments.includes('INTRO_RATE_LOAN')) &&
        product === INSTALLMENT_LOAN_MONTHLY;
    var waivedInterestPeriodDaysLeft = differenceInCalendarDays(new Date(waivedInterest === null || waivedInterest === void 0 ? void 0 : waivedInterest.promoEndDate), new Date());
    var introDateLoanPeriodDaysLeft = differenceInCalendarDays(new Date(introRateLoan === null || introRateLoan === void 0 ? void 0 : introRateLoan.promoExpiryDate), new Date());
    var introDateLoanPeriodDaysSinceExpiry = differenceInCalendarDays(new Date(), new Date(introRateLoan === null || introRateLoan === void 0 ? void 0 : introRateLoan.promoExpiryDate));
    var isIntroRateLoanExpired = ((_b = (_a = billing === null || billing === void 0 ? void 0 : billing.introRateLoan) === null || _a === void 0 ? void 0 : _a.promoStatus) === null || _b === void 0 ? void 0 : _b.includes('EXPIRED')) &&
        introDateLoanPeriodDaysSinceExpiry <= 30 &&
        introDateLoanPeriodDaysSinceExpiry >= 0;
    var isIntroRateLoanExpiring = ((_d = (_c = billing === null || billing === void 0 ? void 0 : billing.introRateLoan) === null || _c === void 0 ? void 0 : _c.promoStatus) === null || _d === void 0 ? void 0 : _d.includes('EXPIRING')) &&
        introDateLoanPeriodDaysLeft <= 30 &&
        introDateLoanPeriodDaysLeft >= 0;
    var isWaivedInterestExpired = waivedInterestPeriodDaysLeft < 0;
    var isWaivedInterestExpiring = waivedInterestPeriodDaysLeft <= 30 && waivedInterestPeriodDaysLeft >= 0;
    var isOpenOrClosedWithBalance = isOpen || isClosedWithBalance;
    var isSetpayAccount = product === INSTALLMENT_LOAN;
    var isPayLaterAccount = product === INSTALLMENT_LOAN_MONTHLY ||
        product === INSTALLMENT_LOAN_PAY_IN_FOUR;
    var isRegisteredAuthProhibited = isAttentionRequired && isRegistered;
    var isEnrolledInPlans = customerAssistancePlan || settlementEnrolled;
    var isNotEnrolledInPlans = !customerAssistancePlan && !settlementEnrolled;
    var isPastDueAndRegistered = pastDue && isRegistered;
    var isPastDueAndUnregistered = pastDue && !isRegistered;
    var isOpenOrClosedEnrolledInPlans = isOpenOrClosedWithBalance && isEnrolledInPlans;
    var isOpenOrClosedNotEnrolledInPlans = isOpenOrClosedWithBalance && isNotEnrolledInPlans;
    var isRegisteredNotEnrolled = isPastDueAndRegistered && isDelinquent && isOpenOrClosedNotEnrolledInPlans;
    var isRegisteredEnrolled = isPastDueAndRegistered && isOpenOrClosedEnrolledInPlans;
    var isUnregisteredNotEnrolled = isPastDueAndUnregistered && isOpenOrClosedNotEnrolledInPlans;
    var isUnregisteredEnrolled = isPastDueAndUnregistered && isOpenOrClosedEnrolledInPlans;
    var isEnrolledNotDelinquent = isOpenOrClosedEnrolledInPlans && !isDelinquent;
    var scheduledAutopayAmount = getScheduledAutopayAmount(accountDetails, isSetpayAccount);
    var isAutopayEnrolled = Number(scheduledAutopayAmount) > 0 && autopayEnrollment;
    var isNoPaymentDue = !pastDue &&
        totalMinimumPaymentDue === '0.00' &&
        (isOpenOrClosedWithBalance || isAttentionRequired);
    var isPaymentDueToday = !pastDue &&
        daysUntilNextPaymentDue === 0 &&
        !(isAutopayEnrolled || isNoPaymentDue);
    var isFuturePaymentDue = !pastDue &&
        daysUntilNextPaymentDue > 0 &&
        !(isAutopayEnrolled || isNoPaymentDue);
    var isPaymentPastDue = daysUntilNextPaymentDue < 0;
    var isCurrentBalanceZero = Number(credit === null || credit === void 0 ? void 0 : credit.currentBalance) <= 0;
    var scheduledActivities = getScheduledActivities(activity);
    var scheduledPaymentStatus = getScheduledPaymentStatus(scheduledActivities, isPayLaterAccount);
    var isAutopayDisplayOnPriority = scheduledActivities.some(function (scheduledActivity) {
        return getNumberOfDaysToScheduledPayment(scheduledActivity) <
            getNumberOfDaysToAutopay(nextPaymentDate);
    });
    var processingActivities = getProcessingActivities(activity);
    var paymentProcessingStatus = getPaymentProcessingStatus(processingActivities);
    var autopayScheduledToday = isAutopayEnrolled && getNumberOfDaysToAutopay(nextPaymentDate) === 0;
    var isPaymentScheduledToday = scheduledActivities.some(function (scheduledActivity) {
        return getNumberOfDaysToScheduledPayment(scheduledActivity) === 0;
    }) || autopayScheduledToday;
    var isCurrentPaymentDue = isPaymentDueToday && !isPaymentScheduledToday;
    var pastDueNotification = [
        isPastDueAndRegistered,
        isRegisteredEnrolled,
        isRegisteredNotEnrolled,
        isUnregisteredNotEnrolled,
        isUnregisteredEnrolled
    ];
    var isPastDue = pastDueNotification.some(function (pastDueNotify) { return pastDueNotify === true; });
    var enrolledInPlanNotification = [
        isRegisteredEnrolled,
        isUnregisteredEnrolled,
        isEnrolledNotDelinquent
    ];
    var isEnrolledInPaymentPlan = enrolledInPlanNotification.some(function (enrolled) { return enrolled === true; });
    var notificationsStatus;
    var isAutopayDateValid = isAutopayEnrolled && !isPaymentPastDue;
    if (isCurrentBalanceZero && isPayLaterAccount && !isPreDelivery) {
        notificationsStatus = [loanPaidOffStatus()];
    }
    else if (isCurrentBalanceZero && isSetpayAccount) {
        notificationsStatus = !isRegistered
            ? [registerAccountStatus(), loanPaidOffStatus()]
            : [loanPaidOffStatus()];
    }
    else if (isCurrentBalanceZero && !isPayLaterAccount) {
        notificationsStatus = [
            isNoPaymentDue && noPaymentDueStatus(),
            !isRegistered && registerAccountStatus(),
            isClosedWithBalance && closedWithBalanceStatus(),
            isEnrolledInPaymentPlan && enrolledOnPaymentPlanStatus(),
            isRegisteredAuthProhibited && attentionRequiredStatus()
        ];
    }
    else if (isAutopayDisplayOnPriority) {
        notificationsStatus = __spreadArray(__spreadArray(__spreadArray(__spreadArray([
            isPastDue && pastDueStatus(isPayLaterAccount)
        ], paymentProcessingStatus, true), [
            isCurrentPaymentDue && currentPaymentDueStatus(),
            isFuturePaymentDue && futurePaymentDueStatus(nextPaymentDate)
        ], false), scheduledPaymentStatus, true), [
            isNoPaymentDue && !isPayLaterAccount && noPaymentDueStatus(),
            isAutopayDateValid &&
                autopayScheduledStatus(scheduledAutopayAmount, getNumberOfDaysToAutopay(nextPaymentDate)),
            isWaivedInterest &&
                isWaivedInterestExpiring &&
                waivedInterestExpiringNotification(waivedInterest),
            isWaivedInterest &&
                isWaivedInterestExpired &&
                waivedInterestExpiredNotification(),
            isIntroRateLoan &&
                isIntroRateLoanExpiring &&
                introRateLoanExpiringNotification(introRateLoan),
            isIntroRateLoan &&
                isIntroRateLoanExpired &&
                introRateLoanExpiredNotification(),
            isPreDelivery &&
                accountDetails.featureFlags.isPreDeliveryMessageEnabled &&
                loanApprovedStillProcessingNotification(),
            !isRegistered && !isPayLaterAccount && registerAccountStatus(),
            isClosedWithBalance && !isPayLaterAccount && closedWithBalanceStatus(),
            isEnrolledInPaymentPlan &&
                !isPayLaterAccount &&
                enrolledOnPaymentPlanStatus(),
            isRegisteredAuthProhibited &&
                !isPayLaterAccount &&
                attentionRequiredStatus()
        ], false);
    }
    else {
        notificationsStatus = __spreadArray(__spreadArray(__spreadArray(__spreadArray([
            isPastDue && pastDueStatus(isPayLaterAccount)
        ], paymentProcessingStatus, true), [
            isCurrentPaymentDue && currentPaymentDueStatus(),
            isFuturePaymentDue && futurePaymentDueStatus(nextPaymentDate)
        ], false), scheduledPaymentStatus, true), [
            isNoPaymentDue && !isPayLaterAccount && noPaymentDueStatus(),
            isAutopayDateValid &&
                autopayScheduledStatus(scheduledAutopayAmount, getNumberOfDaysToAutopay(nextPaymentDate)),
            isWaivedInterest &&
                isWaivedInterestExpiring &&
                waivedInterestExpiringNotification(waivedInterest),
            isWaivedInterest &&
                isWaivedInterestExpired &&
                waivedInterestExpiredNotification(),
            isIntroRateLoan &&
                isIntroRateLoanExpiring &&
                introRateLoanExpiringNotification(introRateLoan),
            isIntroRateLoan &&
                isIntroRateLoanExpired &&
                introRateLoanExpiredNotification(),
            isPreDelivery &&
                accountDetails.featureFlags.isPreDeliveryMessageEnabled &&
                loanApprovedStillProcessingNotification(),
            !isRegistered && !isPayLaterAccount && registerAccountStatus(),
            isClosedWithBalance && !isPayLaterAccount && closedWithBalanceStatus(),
            isEnrolledInPaymentPlan &&
                !isPayLaterAccount &&
                enrolledOnPaymentPlanStatus(),
            isRegisteredAuthProhibited &&
                !isPayLaterAccount &&
                attentionRequiredStatus()
        ], false);
    }
    return notificationsStatus.filter(function (eachStatus) { return eachStatus; });
};
export default getNotifications;
